import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import { Calendar, FileText, ChevronDown, RefreshCw } from "lucide-react";
import { API, axios, formatIcelandicNumber } from "@/utils/constants";
import { useAuth } from "@/context/AuthContext";

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [settings, setSettings] = useState({ vextir: 17.7 });

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, loansRes, settingsRes] = await Promise.allSettled([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/loans`),
          axios.get(`${API}/settings`),
        ]);
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        if (loansRes.status === 'fulfilled') setApplications(loansRes.value.data);
        if (settingsRes.status === 'fulfilled') setSettings(settingsRes.value.data);
      } catch { /* handled */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  /* ─── calculations ─── */
  const calcs = useMemo(() => {
    const today = new Date();
    let nextMonth, nextYear;
    if (today.getMonth() === 11) { nextMonth = 1; nextYear = today.getFullYear() + 1; }
    else { nextMonth = today.getMonth() + 2; nextYear = today.getFullYear(); }

    let outstandingDebt = 0, nextTotal = 0, nextInterest = 0;
    let paid = 0, unpaid = 0, totalPaidAmount = 0, totalUnpaidAmount = 0;
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    const icelandicMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'Maí', 'Jún', 'Júl', 'Ágú', 'Sep', 'Okt', 'Nóv', 'Des'];
    const revenueData = [];
    for (let i = 0; i < 12; i++) {
      const md = new Date(currentYear, today.getMonth() + i, 1);
      revenueData.push({ month: `${icelandicMonths[md.getMonth()]} ${md.getFullYear()}`, monthNum: md.getMonth() + 1, year: md.getFullYear(), revenue: 0 });
    }

    applications.forEach(app => {
      if (app.status !== 'disbursed') return;
      const rem = app.remaining_balance ?? app.loan_amount;
      outstandingDebt += rem;
      const ps = app.payment_schedule || [];
      const st = app.scheduled_payments_status || {};
      ps.forEach(p => {
        if (!p.date) return;
        const parts = p.date.includes('.') ? p.date.split('.') : p.date.split('/');
        if (parts.length !== 3) return;
        const [d, m, y] = parts;
        const pm = parseInt(m), py = parseInt(y);
        const status = st[String(p.month)] || 'pending';
        if (pm === nextMonth && py === nextYear && status !== 'paid') {
          nextTotal += p.payment || 0; nextInterest += p.interest || 0;
        }
        if (pm === currentMonth && py === currentYear) {
          if (status === 'paid') { paid++; totalPaidAmount += p.payment || 0; }
          else { unpaid++; totalUnpaidAmount += p.payment || 0; }
        }
        const idx = revenueData.findIndex(r => r.monthNum === pm && r.year === py);
        if (idx !== -1 && status !== 'paid') revenueData[idx].revenue += p.payment || 0;
      });
    });

    // Add cumulative line
    let cumulative = 0;
    revenueData.forEach(r => { cumulative += r.revenue; r.cumulative = cumulative; });

    const totalInvoices = paid + unpaid || 1;
    const forecastTotal = outstandingDebt + totalUnpaidAmount + totalPaidAmount;
    const outPct = forecastTotal > 0 ? Math.round((outstandingDebt / forecastTotal) * 100) : 0;
    const unpaidPct = forecastTotal > 0 ? Math.round((totalUnpaidAmount / forecastTotal) * 100) : 0;
    const paidPct = 100 - outPct - unpaidPct;

    return {
      outstandingDebt: Math.round(outstandingDebt),
      nextTotal: Math.round(nextTotal), nextInterest: Math.round(nextInterest),
      paid, unpaid, totalPaidAmount: Math.round(totalPaidAmount), totalUnpaidAmount: Math.round(totalUnpaidAmount),
      revenueData, totalInvoices,
      forecastTotal: Math.round(forecastTotal), outPct, unpaidPct, paidPct,
    };
  }, [applications]);

  const ahk = (35 + (settings.styrivextir || 7.25)).toFixed(2);

  const dateRange = useMemo(() => {
    const now = new Date();
    const icMonths = ['Janúar', 'Febrúar', 'Mars', 'Apríl', 'Maí', 'Júní', 'Júlí', 'Ágúst', 'September', 'Október', 'Nóvember', 'Desember'];
    const start = `${icMonths[now.getMonth()]} ${now.getFullYear()}`;
    const end = new Date(now.getFullYear() + 1, now.getMonth(), 1);
    const endStr = `${icMonths[end.getMonth()].toUpperCase()} ${end.getFullYear()}`;
    return `${start} - ${endStr}`;
  }, []);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-sm text-slate-500">Hleð stjórnborði...</div>;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const v = payload[0]?.value || 0;
    return (
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 px-4 py-3">
        <div className="text-base font-bold text-slate-900">{formatIcelandicNumber(Math.round(v))} kr.</div>
      </div>
    );
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="dashboard">
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>Stjórnborð</h1>
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2 mb-6 text-sm text-slate-500">
          <Calendar className="h-4 w-4" />
          <span>{dateRange}</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </div>

        {/* ─── 3 Stat Cards ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6" data-testid="primary-stats">
          {/* Útistandandi skuld */}
          <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm cursor-pointer hover:shadow-md transition-all"
            onClick={() => navigate('/utistandandi-skuld')} data-testid="stat-útistandandi-skuld">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium text-slate-500">Útistandandi skuld</div>
                <div className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{formatIcelandicNumber(calcs.outstandingDebt)} kr.</div>
                <div className="mt-2 flex items-center gap-1 text-sm text-emerald-600">
                  <span className="text-xs">&#8593;</span> {formatIcelandicNumber(calcs.nextInterest)} kr. síðustu 30 dagar
                </div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
            </div>
          </div>

          {/* Næsti mánuður */}
          <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm cursor-pointer hover:shadow-md transition-all"
            onClick={() => navigate('/naesti-manudur')} data-testid="stat-næsti-mánuður">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium text-slate-500">Næsti mánuður</div>
                <div className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{formatIcelandicNumber(calcs.nextTotal)} kr.</div>
                <div className="mt-2 flex items-center gap-1 text-sm text-emerald-600">
                  <span className="text-xs">&#8593;</span> {formatIcelandicNumber(calcs.nextInterest)} kr. frá síðasta mánuði
                </div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-emerald-500" />
              </div>
            </div>
          </div>

          {/* Hámarks ÁHK */}
          <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm cursor-pointer hover:shadow-md transition-all"
            onClick={async () => {
              toast.loading("Sæki stýrivexti...", { id: 'ahk' });
              try {
                const res = await axios.post(`${API}/settings/refresh-styrivextir`);
                if (res.data.success) {
                  setSettings(p => ({ ...p, styrivextir: res.data.styrivextir }));
                  toast.success(`ÁHK uppfært: ${res.data.ahk.toFixed(2)}%`, { id: 'ahk' });
                }
              } catch { toast.error("Villa", { id: 'ahk' }); }
            }}
            data-testid="stat-hámarks-áhk">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium text-slate-500">Hámarks ÁHK</div>
                <div className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
                  {ahk}<span className="text-lg ml-0.5">%</span>
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  35% + {settings.styrivextir || 7.25}% stýrivív.
                </div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 text-orange-500" />
              </div>
            </div>
          </div>
        </div>

        {/* ─── Charts Row ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-5">
          {/* Mánaðartekjur — bar + line */}
          <div className="lg:col-span-8 rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900">Mánaðartekjur</h2>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={calcs.revenueData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `${Math.round(v / 1000)}k`} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" fill="#93bbfd" radius={[6, 6, 0, 0]} barSize={36} />
                  <Line type="monotone" dataKey="cumulative" stroke="#2563eb" strokeWidth={2.5} dot={{ fill: '#2563eb', r: 4, strokeWidth: 2, stroke: '#fff' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right panels */}
          <div className="lg:col-span-4 flex flex-col gap-5">
            {/* Uppgjör */}
            <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm cursor-pointer hover:shadow-md"
              onClick={() => navigate('/ogreiddir-reikningar')} data-testid="uppgjor-panel">
              <h2 className="text-base font-bold text-slate-900 mb-4">Uppgjör</h2>
              <div className="flex items-center gap-5">
                {/* Donut */}
                <div className="w-24 h-24 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={[{ v: calcs.paid }, { v: calcs.unpaid || 0.01 }]} dataKey="v" cx="50%" cy="50%" innerRadius={28} outerRadius={40} startAngle={90} endAngle={-270} paddingAngle={2}>
                        <Cell fill="#10b981" />
                        <Cell fill="#6366f1" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-slate-900">{calcs.paid + calcs.unpaid}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-3xl font-bold text-slate-900">{calcs.paid}</div>
                  <div className="text-sm text-slate-500">Greitt</div>
                  <div className="flex items-center gap-1 text-sm text-emerald-600 mt-1">
                    <span className="text-xs">&#8593;</span> {formatIcelandicNumber(calcs.totalPaidAmount)}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 inline-block" />
                    <span className="text-slate-500">Í vanskil</span>
                    <span className="text-xs text-emerald-600">&#8593;</span>
                    <span className="text-slate-700 font-medium">{formatIcelandicNumber(calcs.totalUnpaidAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Spá */}
            <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
              <h2 className="text-base font-bold text-slate-900 mb-1">Spá</h2>
              <div className="text-3xl font-bold text-slate-900 mb-4">{formatIcelandicNumber(calcs.forecastTotal)} kr.</div>
              <div className="space-y-2.5">
                {[
                  { label: 'Útistandandi', pct: calcs.outPct, amount: calcs.outstandingDebt, color: 'bg-blue-600' },
                  { label: 'Í vanskil', pct: calcs.unpaidPct, amount: calcs.totalUnpaidAmount, color: 'bg-indigo-400' },
                  { label: 'Greitt', pct: calcs.paidPct, amount: calcs.totalPaidAmount, color: 'bg-emerald-500' },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-sm ${item.color} inline-block`} />
                      <span className="text-slate-600">{item.label} {item.pct}%</span>
                    </div>
                    <span className="font-semibold text-slate-900">{formatIcelandicNumber(item.amount)} kr.</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1.5 mt-4 text-xs text-slate-400">
                <Calendar className="h-3.5 w-3.5" />
                Spá byggð á næstu 2 mánuðum
              </div>
            </div>
          </div>
        </div>

        {/* ─── Bottom Growth Chart ─── */}
        <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-emerald-600 text-xs">&#8593;</span>
            <span className="text-2xl font-bold text-emerald-600">67.3%</span>
            <span className="text-base text-slate-600 font-medium">frá fyrra ári</span>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={calcs.revenueData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="areaBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#93bbfd" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#93bbfd" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `${Math.round(v / 1000)}k`} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="cumulative" stroke="#93bbfd" strokeWidth={2} fill="url(#areaBlue)" dot={{ fill: '#fff', stroke: '#93bbfd', r: 4, strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
